import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { setToken } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

/**
 * Handles Google OAuth callback.
 * Reads session_id from URL fragment, exchanges it via backend, then redirects to correct dashboard.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const { bootstrap } = useAuth();
  const processed = useRef(false);
  const [msg, setMsg] = useState("Signing you in…");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");
    const preferredRole = localStorage.getItem("recruvo_preferred_role") || "candidate";

    if (!sessionId) {
      toast.error("Missing Google session");
      nav("/login");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", {
          session_id: sessionId,
          preferred_role: preferredRole,
        });
        localStorage.removeItem("recruvo_preferred_role");
        // Store session_token for Authorization header (backup for cookie)
        setToken(data.session_token);
        // Refresh AuthProvider state with the new user
        await bootstrap();

        const role = data.user.role;
        const dash =
          role === "admin" ? "/dashboard/agency"
          : role === "employer" ? "/dashboard/employer"
          : "/dashboard/candidate";
        toast.success(data.is_new ? `Welcome to Recruvo, ${data.user.name.split(" ")[0]}!` : `Welcome back, ${data.user.name.split(" ")[0]}!`);
        // Clean the URL and go to dashboard
        window.history.replaceState({}, "", "/");
        nav(dash, { replace: true, state: { user: data.user } });
      } catch (e) {
        console.error(e);
        toast.error(e.response?.data?.detail || "Google sign-in failed");
        nav("/login");
      }
    })();
  }, [nav, bootstrap]);

  return (
    <div className="min-h-screen grid place-items-center bg-navy text-white">
      <div className="text-center">
        <div className="h-12 w-12 rounded-full border-4 border-gold border-t-transparent animate-spin mx-auto" />
        <div className="mt-4 font-display text-xl">{msg}</div>
        <div className="text-sm text-slate-300 mt-1">Setting up your Recruvo account</div>
      </div>
    </div>
  );
}
